import React, { useState, useEffect } from 'react';
import { Search, Bell, Sun, Moon, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const AdminHeader = ({ role, isDark, toggleDark, onRoleSwitch,isSuperAdmin: userIsSuperAdmin }) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState(''); // State for search input
  const [searchResults, setSearchResults] = useState([]); // State for search results

  useEffect(() => {
    if (searchQuery && searchQuery.length > 1) {
      const query = searchQuery.toLowerCase();
      
      // Query common text-bearing elements in the dashboard
      const elements = Array.from(document.querySelectorAll('h3, p, td, th'));
      
      const results = [];
      const seen = new Set();

      elements.forEach((el) => {
        const text = el.innerText || el.textContent;
        if (text && text.toLowerCase().includes(query)) {
          let label = text.replace(/\n/g, ' ').trim();
          if (label.length > 50) label = label.substring(0, 50) + '...';
          
          if (label && !seen.has(label)) {
            seen.add(label);
            results.push({ label, element: el });
          }
        }
      });

      setSearchResults(results.slice(0, 8));
    } else {
      setSearchResults([]);
    }
  }, [searchQuery]);

  const border = isDark ? '#1e2d52' : '#e2e8f0';
  const isSuperAdmin = role === 'superadmin';

  const handleResultClick = (result) => {
    if (result && result.element) {
      result.element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      
      // Temporary highlight
      const originalBg = result.element.style.backgroundColor;
      const originalTransition = result.element.style.transition;
      
      result.element.style.transition = 'background-color 0.4s ease';
      result.element.style.backgroundColor = isDark ? 'rgba(248,114,23,0.4)' : 'rgba(248,114,23,0.3)';
      
      setTimeout(() => {
        result.element.style.backgroundColor = originalBg;
        setTimeout(() => {
          result.element.style.transition = originalTransition;
        }, 400);
      }, 1500);
    }
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (searchResults.length > 0) {
        handleResultClick(searchResults[0]);
      } else {
        // Native fallback
        window.find(searchQuery, false, false, true, false, true, false);
      }
    }
  };

  return (
    <header
      className="flex-shrink-0 flex items-center justify-between px-6 py-3 border-b"
      style={{
        background: isDark
          ? 'linear-gradient(135deg, #0f1729 0%, #0a0f1e 100%)'
          : 'linear-gradient(135deg, #fff 0%, #fff8f5 100%)',
        borderColor: border,
        minHeight: 60,
      }}
    >
      {/* Left: breadcrumb */}
      <div className="flex items-center gap-3">
        <button 
          onClick={() => navigate('/dashboard')}
          className={`p-2 rounded-xl transition-colors ${
            isDark ? 'hover:bg-white/10 text-slate-400 hover:text-white' : 'hover:bg-slate-100 text-slate-500 hover:text-slate-900'
          }`}
          title="Back to User Dashboard"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <p className={`text-xs font-medium ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
            Dashboard /
          </p>
          <h1
            className="text-base font-bold font-['Outfit'] leading-tight"
            style={{ color: isDark ? '#f0f6fc' : '#0d1a3a' }}
          >
            {isSuperAdmin ? 'Super Admin Overview' : 'Admin Overview'}
          </h1>
        </div>
      </div>

      {/* Center: Search */}
      <div className="hidden md:flex items-center gap-2 flex-1 max-w-sm mx-8 relative">
        <div
          className="flex items-center gap-2 flex-1 px-3 py-2 rounded-xl text-sm"
          style={{
            background: isDark ? '#162040' : '#f8fafc',
            border: `1px solid ${isDark ? '#1e2d52' : '#e2e8f0'}`,
            color: isDark ? '#94a3b8' : '#64748b',
          }}
        >
          <Search className="w-3.5 h-3.5 flex-shrink-0" />
          <input
            type="text"
            placeholder="Search users, agents…"
            className="bg-transparent outline-none flex-1 text-xs"
            style={{
              color: isDark ? '#c9d1d9' : '#475569',
              '--tw-placeholder-color': isDark ? '#64748b' : '#94a3b8',
            }}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <span
            className="text-[10px] px-1.5 py-0.5 rounded font-mono"
            style={{
              border: `1px solid ${isDark ? '#2d3f6e' : '#cbd5e1'}`,
              color: isDark ? '#475569' : '#94a3b8',
            }}
          >
            ⌘K
          </span>
        </div>
      </div>

      {/* Display search results */}
      {searchQuery && (
        <div
          className="absolute top-16 left-1/2 transform -translate-x-1/2 shadow-lg rounded-lg p-2 w-96 z-50 border"
          style={{ 
            background: isDark ? '#1e2d52' : '#ffffff', 
            borderColor: isDark ? '#2d3f6e' : '#e2e8f0',
            color: isDark ? '#c9d1d9' : '#475569' 
          }}
        >
          <h3 className="text-xs font-semibold mb-2 px-2 pt-1 text-slate-400">Search Results</h3>
          {searchResults.length > 0 ? (
            <ul className="flex flex-col gap-1">
              {searchResults.map((result, index) => (
                <li 
                  key={index} 
                  className={`text-xs px-3 py-2 rounded-md cursor-pointer transition-colors ${
                    isDark ? 'hover:bg-[#162040]' : 'hover:bg-slate-100'
                  }`}
                  onClick={() => handleResultClick(result)}
                >
                  {result.label}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs px-2 pb-1">No results found.</p>
          )}
        </div>
      )}

      {/* Right: controls */}
      <div className="flex items-center gap-2">
        
        {/* Role switcher — only show Super Admin tab if user is superadmin */}
      <div className="flex items-center rounded-xl p-0.5"
        style={{
          border: `1px solid ${isDark ? '#1e2d52' : '#e2e8f0'}`,
          background: isDark ? '#162040' : '#f1f5f9',
        }}
      >
        <button
          onClick={() => onRoleSwitch('admin')}
          className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
          style={{
            background: !isSuperAdmin ? '#F87217' : 'transparent',
            color: !isSuperAdmin ? '#ffffff' : (isDark ? '#94a3b8' : '#64748b'),
            boxShadow: !isSuperAdmin ? '0 1px 4px rgba(248,114,23,0.3)' : 'none',
          }}
        >
          Admin
        </button>
        {userIsSuperAdmin && (
          <button
            onClick={() => onRoleSwitch('superadmin')}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
            style={{
              background: isSuperAdmin ? '#F87217' : 'transparent',
              color: isSuperAdmin ? '#ffffff' : (isDark ? '#94a3b8' : '#64748b'),
              boxShadow: isSuperAdmin ? '0 1px 4px rgba(248,114,23,0.3)' : 'none',
            }}
          >
            Super Admin
          </button>
        )}
      </div>

        {/* Bell */}
        <button
          className={`relative p-2 rounded-xl transition-colors ${isDark ? 'text-slate-400 hover:bg-white/10 hover:text-white' : 'text-slate-500 hover:bg-slate-100'
            }`}
        >
          <Bell className="w-4 h-4" />
          <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-red-500" />
        </button>

        {/* Dark mode */}
        <button
          onClick={toggleDark}
          className={`p-2 rounded-xl transition-colors ${isDark ? 'text-slate-400 hover:bg-white/10 hover:text-white' : 'text-slate-500 hover:bg-slate-100'
            }`}
          title={isDark ? 'Light mode' : 'Dark mode'}
        >
          {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
        </button>

        {/* Avatar */}
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#F87217] to-[#FF4F00] flex items-center justify-center shadow">
          <span className="text-white text-xs font-bold">
            {isSuperAdmin ? 'SA' : 'AD'}
          </span>
        </div>
      </div>
    </header>
  );
};

export default AdminHeader;